import React, { useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Button,
    Box,
    Paper,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Typography,
    LinearProgress,
} from '@mui/material';
import { postData } from "../api/axiosConfig";
import {
    CircleX,
    FileText,
    Folder,
    FolderOpen,
    Cloud,
    Upload as UploadIcon,
    Lock,
    Files,
} from 'lucide-react';
import { useSnackbar } from '../context/SnackbarContext';
import SharepointIcon from '../assets/images/sharepoint-48.png';
import OneDriveIcon from '../assets/images/onedrive-48.png';
import { MSAccess } from './MSAccess';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from '../context/AuthContext';

export const Upload = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null); // { current, total, results }
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState('local');
    const [selectedSource, setSelectedSource] = useState('onedrive');
    const [pickerOpen, setPickerOpen] = useState(false);
    // User info from Google OAuth authentication
    const userInfo = { email: user?.email || 'anonymous@user.local', name: user?.name || user?.given_name || 'User' };

    const VALID_EXTENSIONS = [".pdf", ".docx"];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

    const handleRemoveFile = (fileId) => {
        setFiles(prev => prev.filter(file => file.job_id !== fileId));
    };

    const handleClearAll = () => {
        setFiles([]);
    };

    const handleFiles = async (newFiles) => {
        const fileArray = Array.from(newFiles);
        if (!fileArray.length) return;

        let added = 0;
        let skippedType = 0;
        let skippedSize = 0;
        let skippedDuplicate = 0;

        const validFiles = [];

        for (const file of fileArray) {
            const hasValidType = VALID_EXTENSIONS.some(ext =>
                file.name.toLowerCase().endsWith(ext)
            );

            if (!hasValidType) {
                skippedType++;
                continue;
            }

            if (file.size > MAX_FILE_SIZE) {
                skippedSize++;
                continue;
            }

            // Check for duplicates (same name and size)
            const isDuplicate = files.some(
                existing => existing.name === file.name && existing.size === file.size
            ) || validFiles.some(
                existing => existing.name === file.name && existing.size === file.size
            );

            if (isDuplicate) {
                skippedDuplicate++;
                continue;
            }

            validFiles.push({
                name: file.name,
                size: file.size,
                job_id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 5)}-${file.name}`,
                file: file,
            });
            added++;
        }

        if (validFiles.length > 0) {
            setFiles(prev => [...prev, ...validFiles]);
        }

        // Build feedback message
        const parts = [];
        if (added > 0) parts.push(`${added} file${added > 1 ? 's' : ''} added`);
        if (skippedType > 0) parts.push(`${skippedType} skipped (unsupported type)`);
        if (skippedSize > 0) parts.push(`${skippedSize} skipped (too large)`);
        if (skippedDuplicate > 0) parts.push(`${skippedDuplicate} skipped (duplicate)`);

        if (parts.length > 0) {
            showSnackbar({
                message: parts.join(', ') + '.',
                severity: added > 0 ? 'success' : 'warning',
                type: 'file',
                duration: 4000
            });
        }
    };

    const handleFolderSelect = (e) => {
        if (e.target.files?.length > 0) {
            handleFiles(e.target.files);
        }
        // Reset the input so the same folder can be re-selected
        e.target.value = '';
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(e.type === "dragenter" || e.type === "dragover");
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files?.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleSubmit = async () => {
        if (!files.length) {
            showSnackbar({
                message: 'Please select files to upload before proceeding.',
                severity: 'warning',
                type: 'upload',
                duration: 4000
            });
            return;
        }

        if (!userInfo?.email) {
            showSnackbar({
                message: 'User authentication required. Please refresh the page and try again.',
                severity: 'error',
                type: 'default',
                duration: 5000
            });
            return;
        }

        setUploading(true);
        setUploadProgress({ current: 0, total: files.length, results: [] });

        try {
            const formData = new FormData();
            files.forEach(fileData => {
                formData.append('files', fileData.file);
            });

            const response = await postData(
                `/api/documents/upload-batch?user_id=${encodeURIComponent(userInfo.email)}`,
                formData
            );

            const succeeded = response.succeeded || 0;
            const skipped = response.skipped || 0;

            setUploadProgress({
                current: files.length,
                total: files.length,
                results: response.results || [],
            });

            if (succeeded > 0) {
                showSnackbar({
                    message: `${succeeded} file${succeeded > 1 ? 's' : ''} uploaded successfully!${skipped > 0 ? ` ${skipped} skipped.` : ''} Processing has started.`,
                    severity: 'success',
                    type: 'upload',
                    duration: 5000
                });
            } else {
                showSnackbar({
                    message: `All files were skipped. Please check file types and sizes.`,
                    severity: 'warning',
                    type: 'upload',
                    duration: 5000
                });
            }

            // Short delay to show final progress before navigating
            setTimeout(() => {
                setUploading(false);
                setUploadProgress(null);
                setFiles([]);
                if (succeeded > 0) {
                    navigate('/files', { state: { fromUpload: true } });
                }
            }, 1200);

        } catch (error) {
            console.error('Upload error:', error);
            showSnackbar({
                message: error.response?.data?.detail || error.message || 'Failed to upload files. Please check your connection and try again.',
                severity: 'error',
                type: 'upload',
                duration: 6000
            });
            setUploading(false);
            setUploadProgress(null);
        }
    }


    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleSourceChange = (source) => {
        setSelectedSource(source);
        setPickerOpen(true);
    };

    // Get current files based on active tab
    const currentFiles = activeTab === 'local' ? files : [];

    return (
        <Card sx={{ minWidth: 275, maxWidth: 1200, margin: 'auto' }} className="rounded-xl shadow-lg relative bg-gradient-to-br from-white to-gray-50 border-0">
            <CardContent sx={{ padding: '1rem' }}>
                <div className="mb-4">
                    <h1 className="text-lg font-bold text-gray-800 mb-0.5">
                        Upload Data
                    </h1>
                    <p className="text-sm text-gray-600 leading-relaxed">Add your documents here. Upload multiple files or entire folders with drag & drop functionality.</p>
                </div>
                <div className="bg-gradient-to-br from-card/95 to-card/85 backdrop-blur-sm rounded-xl border border-border/40 shadow-strong p-6">
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                        {/* Tab Navigation */}
                        <div className="flex justify-center mb-6">
                            <TabsList className="inline-flex bg-gray-100 rounded-lg p-1 gap-1">
                                <TabsTrigger
                                    value="local"
                                    className="flex items-center gap-2 text-sm py-2 px-4"
                                >
                                    <Folder className="w-4 h-4" />
                                    Local Files
                                </TabsTrigger>
                                <TabsTrigger
                                    value="cloud"
                                    className="flex items-center gap-2 text-sm py-2 px-4"
                                >
                                    <Cloud className="w-4 h-4" />
                                    Cloud
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        {/* Local Files Tab */}
                        <TabsContent value="local" className="space-y-4">
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem' }}>
                                {/* Hidden file input - accepts multiple */}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={(e) => {
                                        handleFiles(e.target.files);
                                        e.target.value = '';
                                    }}
                                    style={{ display: 'none' }}
                                    accept=".pdf,.docx"
                                    multiple
                                />
                                {/* Hidden folder input */}
                                <input
                                    type="file"
                                    ref={folderInputRef}
                                    onChange={handleFolderSelect}
                                    style={{ display: 'none' }}
                                    webkitdirectory=""
                                    directory=""
                                    multiple
                                />
                                {/* Drop Zone */}
                                <Paper
                                    variant="outlined"
                                    className="upload-zone hover-scale"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "24px 32px",
                                        bgcolor: dragActive ? "primary.50" : "background.paper",
                                        cursor: "pointer",
                                        transition: "all 0.3s",
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            bgcolor: "primary.50",
                                            transform: "translateY(-2px)",
                                        },
                                        overflow: "hidden",
                                        minHeight: "220px",
                                        borderRadius: "12px",
                                        border: "2px dashed",
                                        borderColor: dragActive ? "primary.main" : "#e2e8f0",
                                        width: "100%",
                                    }}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => !uploading && fileInputRef.current?.click()}
                                >
                                    {uploading ? (
                                        <div className="text-center" style={{ width: '100%', maxWidth: 400 }}>
                                            <CircularProgress size={48} sx={{ mb: 2 }} />
                                            <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                                                Uploading & Processing {files.length} file{files.length > 1 ? 's' : ''}...
                                            </Typography>
                                            {uploadProgress && (
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={(uploadProgress.current / uploadProgress.total) * 100}
                                                    sx={{ borderRadius: 4, height: 6, mt: 1 }}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className={`w-16 h-16 mx-auto rounded-lg bg-primary/10 flex items-center justify-center mb-4 transition-all duration-300 ${dragActive ? 'scale-110 bg-primary/20' : ''
                                                }`}>
                                                <UploadIcon className="w-8 h-8 text-primary" />
                                            </div>

                                            <div className="text-center space-y-3">
                                                <Typography variant="h6" className="font-heading font-semibold text-foreground">
                                                    Drag your files or folder to start uploading
                                                </Typography>
                                                <Typography variant="body2" className="text-muted-foreground font-medium">
                                                    OR
                                                </Typography>
                                                <div className="flex justify-center gap-3">
                                                    <Button
                                                        variant="outlined"
                                                        className="!rounded-lg px-5 py-2 my-3 !text-sm !capitalize border-primary/30 text-primary hover:bg-primary/5 hover-scale"
                                                        startIcon={<Files size={16} />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            fileInputRef.current?.click();
                                                        }}
                                                    >
                                                        Browse Files
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        className="!rounded-lg px-5 py-2 my-3 !text-sm !capitalize border-primary/30 text-primary hover:bg-primary/5 hover-scale"
                                                        startIcon={<FolderOpen size={16} />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            folderInputRef.current?.click();
                                                        }}
                                                    >
                                                        Upload Folder
                                                    </Button>
                                                </div>

                                                {/* File Format Badges */}
                                                <div className="flex justify-center gap-2 flex-wrap mt-3">
                                                    {["PDF", "DOCX"].map((format) => (
                                                        <span key={format} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                                                            {format}
                                                        </span>
                                                    ))}
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-border">
                                                        Max 10MB each
                                                    </span>
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-border">
                                                        Multiple files
                                                    </span>
                                                </div>

                                                {/* Security Notice */}
                                                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-4">
                                                    <Lock className="w-3 h-3" />
                                                    <span>Your documents are securely stored in our encrypted environment.</span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </Paper>
                                <div className="text-xs text-gray-500 text-center mt-3 bg-gray-50 py-2 px-3 rounded-lg">
                                    <span className="font-medium">Supported formats:</span> PDF, DOCX files up to 10MB each · Multiple files & folder upload supported
                                </div>
                            </Box>

                            {files.length > 0 && (
                                <div className="mt-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-semibold text-gray-700">
                                            Selected Files ({files.length})
                                        </h3>
                                        {files.length > 1 && (
                                            <Button
                                                size="small"
                                                onClick={handleClearAll}
                                                disabled={uploading}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '0.75rem',
                                                    color: '#ef4444',
                                                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' }
                                                }}
                                            >
                                                Clear All
                                            </Button>
                                        )}
                                    </div>
                                    <Paper variant="outlined" sx={{
                                        bgcolor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        maxHeight: '280px',
                                        overflow: 'auto',
                                    }}>
                                        <List dense sx={{ padding: '0.25rem' }}>
                                            {files.map((file, index) => (
                                                <ListItem
                                                    key={file.job_id}
                                                    sx={{
                                                        bgcolor: 'white',
                                                        borderRadius: '6px',
                                                        mb: 0.5,
                                                        border: '1px solid #f1f5f9',
                                                        '&:hover': {
                                                            bgcolor: '#f8fafc',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                        },
                                                        transition: 'all 0.2s ease',
                                                        py: 0.5
                                                    }}
                                                    secondaryAction={
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleRemoveFile(file.job_id)}
                                                            disabled={uploading}
                                                            sx={{
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                                                                    color: '#ef4444'
                                                                }
                                                            }}
                                                        >
                                                            <CircleX size={18} className="text-red-500" />
                                                        </IconButton>
                                                    }
                                                >
                                                    <ListItemIcon>
                                                        <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                                                            <FileText size={14} className="text-blue-600" />
                                                        </div>
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={file.name}
                                                        secondary={`${(file.size / 1024).toFixed(2)} KB`}
                                                        primaryTypographyProps={{
                                                            className: "text-xs font-medium text-gray-800",
                                                            sx: { fontWeight: 500 }
                                                        }}
                                                        secondaryTypographyProps={{
                                                            className: "text-xs text-gray-500"
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </div>
                            )}
                        </TabsContent>

                        {/* Cloud Files Tab */}
                        <TabsContent value="cloud" className="space-y-4">
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                    py: 4,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Choose a cloud service to browse and select files
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    {/* OneDrive Card */}
                                    <Box
                                        onClick={() => handleSourceChange('onedrive')}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 3,
                                            width: 160,
                                            borderRadius: '16px',
                                            border: '2px solid',
                                            borderColor: '#e2e8f0',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            bgcolor: 'white',
                                            '&:hover': {
                                                borderColor: '#0078d4',
                                                bgcolor: '#0078d410',
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 8px 24px rgba(0,120,212,0.15)',
                                            },
                                        }}
                                    >
                                        <img src={OneDriveIcon} alt="OneDrive" style={{ width: 48, height: 48 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#0078d4' }}>
                                            OneDrive
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            Browse your personal files
                                        </Typography>
                                    </Box>

                                    {/* SharePoint Card */}
                                    <Box
                                        onClick={() => handleSourceChange('sharepoint')}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 3,
                                            width: 160,
                                            borderRadius: '16px',
                                            border: '2px solid',
                                            borderColor: '#e2e8f0',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            bgcolor: 'white',
                                            '&:hover': {
                                                borderColor: '#038387',
                                                bgcolor: '#03838710',
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 8px 24px rgba(3,131,135,0.15)',
                                            },
                                        }}
                                    >
                                        <img src={SharepointIcon} alt="SharePoint" style={{ width: 48, height: 48 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#038387' }}>
                                            SharePoint
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                            Browse team sites
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* File picker modal */}
                            <MSAccess
                                open={pickerOpen}
                                uploadType={selectedSource}
                                userInfo={userInfo}
                                showSnackbar={showSnackbar}
                                onClose={() => setPickerOpen(false)}
                            />
                        </TabsContent>
                    </Tabs>

                    {/* Action Buttons */}
                    <div className="mt-6 flex items-center justify-center gap-4">
                        <Button
                            variant="outlined"
                            className="px-6 py-2 text-sm rounded-lg border-gray-300 text-gray-600 hover:bg-gray-50 hover-scale"
                            onClick={() => {
                                setFiles([]);
                                setActiveTab('local');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={currentFiles.length === 0 || uploading}
                            variant="contained"
                            className="px-6 py-2 text-sm rounded-lg btn-gradient hover-scale disabled:opacity-50"
                            sx={{
                                '&.Mui-disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            {uploading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                    Uploading & Processing...
                                </>
                            ) : (
                                <>Upload {files.length > 0 ? `${files.length} File${files.length > 1 ? 's' : ''}` : 'Files'}</>
                            )}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
};